import { ArchiveIcon, WarningIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Interruptor,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import { ApiError, type CardBrand, type CardInput, type CreditCard, type Member } from '@/lib/api'
import { haptic } from '@/lib/haptics'
import {
  useActualizarTarjeta,
  useArchivarTarjeta,
  useCards,
  useCrearTarjeta,
  useHousehold,
} from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Alta y edición de una tarjeta de crédito.
 *
 * Es el mismo formulario en los dos casos a propósito: los campos que definen
 * una tarjeta no cambian por darla de alta o por corregirla, y mantener dos
 * pantallas paralelas garantizaba que una acabara con un campo que la otra no
 * tiene.
 *
 * Los cuatro campos del ciclo —día de corte, plazo, tipo de plazo y si el gasto
 * del día del corte entra— son los que gobiernan TODAS las fechas que la app
 * anuncia. Por eso cada uno lleva su explicación al lado: quien los captura mal
 * no ve un error, ve un recordatorio que llega tarde.
 */

const MARCAS: ReadonlyArray<OpcionSegmento<CardBrand>> = [
  { value: 'VISA', label: 'Visa' },
  { value: 'MASTERCARD', label: 'Mastercard' },
  { value: 'AMEX', label: 'American Express' },
  { value: 'OTHER', label: 'Otra' },
]

const TIPOS_PLAZO: ReadonlyArray<OpcionSegmento<'BUSINESS_DAYS' | 'CALENDAR_DAYS'>> = [
  {
    value: 'BUSINESS_DAYS',
    label: 'Días hábiles',
    detalle: 'Salta fines de semana y festivos bancarios',
  },
  { value: 'CALENDAR_DAYS', label: 'Días naturales', detalle: 'Cuenta corridos, sin excepciones' },
]

/** Valor del selector de dueño cuando la tarjeta es del fondo común. */
const COMUN = '__comun__'

export function CardForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { membership } = useSession()

  const tarjetas = useCards()
  const hogar = useHousehold()

  const cerrar = (): void => void navigate('/tarjetas')

  // Crear, editar y archivar son de administración. Se corta aquí y no en el
  // botón de guardar: dejar rellenar un formulario entero para que el servidor
  // responda 403 al final es gastarle el tiempo a alguien a cambio de nada.
  if (membership.role !== 'ADMIN') {
    return (
      <FormSheet tinte="lavender" titulo={id ? 'Editar tarjeta' : 'Nueva tarjeta'} onCerrar={cerrar} accion={null}>
        <ErrorState
          titulo="Solo quien administra puede tocar las tarjetas"
          mensaje="Pídele a quien administra el hogar que la dé de alta o la modifique. Registrar un pago sí lo puedes hacer tú desde la pantalla de Tarjetas."
        />
      </FormSheet>
    )
  }

  const cargando = hogar.isPending || (id !== undefined && tarjetas.isPending)
  if (cargando) {
    return (
      <FormSheet tinte="lavender" titulo={id ? 'Editar tarjeta' : 'Nueva tarjeta'} onCerrar={cerrar} accion={null}>
        <Skeleton className="h-[72px]" />
        <Skeleton className="h-[72px]" />
        <Skeleton className="h-[110px]" />
        <Skeleton className="h-[240px]" />
      </FormSheet>
    )
  }

  const falloCarga = hogar.isError || (id !== undefined && tarjetas.isError)
  if (falloCarga || !hogar.data) {
    return (
      <FormSheet tinte="lavender" titulo={id ? 'Editar tarjeta' : 'Nueva tarjeta'} onCerrar={cerrar} accion={null}>
        <ErrorState
          titulo="No pudimos abrir el formulario"
          mensaje="Necesitamos los datos de tu hogar para saber de quién será la tarjeta."
          onReintentar={() => {
            void hogar.refetch()
            void tarjetas.refetch()
          }}
        />
      </FormSheet>
    )
  }

  const card = id ? (tarjetas.data ?? []).find((c) => c.id === id) : undefined
  if (id && !card) {
    return (
      <FormSheet tinte="lavender" titulo="Editar tarjeta" onCerrar={cerrar} accion={null}>
        <ErrorState
          titulo="No encontramos esa tarjeta"
          mensaje="Puede que la hayan archivado desde otro dispositivo."
          onReintentar={() => void tarjetas.refetch()}
        />
      </FormSheet>
    )
  }

  // El estado inicial se calcula una sola vez, al montar el formulario. La
  // `key` lo remonta si cambia la tarjeta, y así una recarga de la lista en
  // segundo plano no puede pisar lo que la persona lleva escrito.
  return (
    <Formulario
      key={card?.id ?? 'nueva'}
      card={card ?? null}
      integrantes={hogar.data.members}
      esPersonal={hogar.data.kind === 'PERSONAL'}
      miMemberId={membership.id}
      onCerrar={cerrar}
    />
  )
}

function Formulario({
  card,
  integrantes,
  esPersonal,
  miMemberId,
  onCerrar,
}: {
  card: CreditCard | null
  integrantes: Member[]
  esPersonal: boolean
  miMemberId: string
  onCerrar: () => void
}) {
  const crear = useCrearTarjeta()
  const actualizar = useActualizarTarjeta()
  const archivar = useArchivarTarjeta()

  const [nombre, setNombre] = useState(card?.name ?? '')
  const [emisor, setEmisor] = useState(card?.issuer ?? '')
  const [marca, setMarca] = useState<CardBrand>(card?.brand ?? 'OTHER')
  const [last4, setLast4] = useState(card?.last4 ?? '')
  const [limiteCents, setLimiteCents] = useState(card?.creditLimitCents ?? 0)
  /**
   * Lo que ya se debe. Solo al dar de alta: después la deuda la mueven los
   * movimientos, y dejar editarla a mano permitiría borrar gastos reales sin
   * rastro.
   */
  const [deudaCents, setDeudaCents] = useState(0)
  const [corte, setCorte] = useState(card ? String(card.statementDay) : '')
  const [plazo, setPlazo] = useState(String(card?.paymentTermDays ?? 20))
  const [tipoPlazo, setTipoPlazo] = useState<'BUSINESS_DAYS' | 'CALENDAR_DAYS'>(
    card?.paymentTermType ?? 'BUSINESS_DAYS',
  )
  const [inclusivo, setInclusivo] = useState(card?.cutoffInclusive ?? false)
  const [dueno, setDueno] = useState<string>(
    card ? (card.ownerMemberId ?? COMUN) : miMemberId,
  )

  // Los errores locales no se pintan hasta el primer intento de guardar: marcar
  // en rojo un formulario que apenas se abrió acusa de un error que nadie ha
  // cometido todavía.
  const [intentado, setIntentado] = useState(false)
  const [confirmandoArchivo, setConfirmandoArchivo] = useState(false)

  const guardando = crear.isPending || actualizar.isPending
  const fallo = crear.error ?? actualizar.error ?? archivar.error

  const local = validar({ nombre, last4, limiteCents, corte, plazo })
  const errorDe = (campo: keyof typeof local): string | undefined => {
    if (intentado && local[campo]) return local[campo]
    // El servidor manda el detalle por campo; es más útil que un «revisa los
    // datos» genérico porque señala cuál de los diez campos hay que corregir.
    if (fallo instanceof ApiError) return fallo.fieldErrors?.[CAMPO_API[campo]]?.join('. ')
    return undefined
  }

  // El fondo común solo tiene sentido si hay con quién compartir. En un hogar
  // personal se ofrece únicamente si la tarjeta ya venía marcada así.
  const opcionesDueno: OpcionSegmento<string>[] = integrantes.map((m) => ({
    value: m.id,
    label: m.user.displayName.split(' ')[0] ?? m.user.displayName,
    detalle: m.id === miMemberId ? 'Tuya' : 'Personal',
  }))
  if (!esPersonal || dueno === COMUN) {
    opcionesDueno.push({ value: COMUN, label: 'Fondo común', detalle: 'De todo el hogar' })
  }

  const guardar = (): void => {
    setIntentado(true)
    if (Object.values(local).some(Boolean)) {
      haptic('error')
      return
    }

    const esComun = dueno === COMUN
    const base: CardInput = {
      name: nombre.trim(),
      brand: marca,
      creditLimitCents: limiteCents,
      ...(card ? {} : { openingBalanceCents: deudaCents }),
      statementDay: Number(corte),
      paymentTermDays: Number(plazo),
      paymentTermType: tipoPlazo,
      cutoffInclusive: inclusivo,
      // `isShared` y `ownerMemberId` nunca viajan juntos: el servidor rechaza
      // la pareja porque una tarjeta del fondo común no puede tener dueño.
      ...(esComun ? { isShared: true } : { isShared: false, ownerMemberId: dueno }),
      // El emisor vacío se manda como cadena vacía al editar para poder
      // borrarlo; al dar de alta se omite.
      ...(emisor.trim() || card ? { issuer: emisor.trim() } : {}),
      // Los 4 dígitos solo se mandan si están completos: el servidor exige el
      // patrón exacto y no admite cadena vacía, así que vaciar el campo en una
      // tarjeta que ya los tenía los deja como estaban.
      ...(last4 ? { last4 } : {}),
    }

    const alGuardar = {
      onSuccess: () => {
        haptic('success')
        onCerrar()
      },
      // Sin limpiar el formulario: volver a capturar diez campos por un fallo
      // de red es la forma más rápida de que alguien abandone.
      onError: () => haptic('error'),
    }

    if (card) actualizar.mutate({ id: card.id, ...base }, alGuardar)
    else crear.mutate(base, alGuardar)
  }

  return (
    <FormSheet
      tinte="lavender"
      titulo={card ? card.name : 'Nueva tarjeta'}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError mensaje={fallo ? mensajeDeError(fallo) : null} />
          <Guardar onClick={guardar} guardando={guardando}>
            {card ? 'Guardar cambios' : 'Añadir tarjeta'}
          </Guardar>
        </div>
      }
    >
      <Campo label="Nombre de la tarjeta" error={errorDe('nombre')}>
        <Texto
          value={nombre}
          onChange={setNombre}
          placeholder="BBVA Azul"
          maxLength={60}
          invalido={Boolean(errorDe('nombre'))}
        />
      </Campo>

      <Campo label="Banco emisor" ayuda="Opcional. Sirve para distinguirla en las listas.">
        <Texto value={emisor} onChange={setEmisor} placeholder="BBVA" maxLength={60} />
      </Campo>

      <Campo label="Marca">
        <Segmentos opciones={MARCAS} value={marca} onChange={setMarca} />
      </Campo>

      <Campo
        label="Últimos 4 dígitos"
        ayuda="Opcional. Solo estos cuatro: PayBae nunca guarda el número completo ni el CVV."
        error={errorDe('last4')}
      >
        <Texto
          value={last4}
          onChange={(v) => setLast4(v.replace(/\D/g, '').slice(0, 4))}
          placeholder="4417"
          inputMode="numeric"
          maxLength={4}
          invalido={Boolean(errorDe('last4'))}
        />
      </Campo>

      <Campo label="Límite de crédito" error={errorDe('limite')}>
        <Monto cents={limiteCents} onChange={setLimiteCents} />
      </Campo>

      {/* Solo al registrar. Una tarjeta que ya se venía usando llega con saldo,
          y sin preguntarlo la app arrancaba creyendo que no debes nada: a
          partir de ahí el dinero libre del inicio era más alto de lo real. */}
      {!card ? (
        <Campo
          label="¿Cuánto debes hoy en esta tarjeta?"
          ayuda="Lo que te aparece como saldo actual. Si está en ceros, déjalo en cero."
        >
          <Monto cents={deudaCents} onChange={setDeudaCents} />
        </Campo>
      ) : null}

      <Campo
        label="Día de corte"
        ayuda="Del 1 al 31. Si pones 31, en los meses que no lo tienen cortamos el último día — nunca se salta un mes."
        error={errorDe('corte')}
      >
        <Texto
          value={corte}
          onChange={(v) => setCorte(v.replace(/\D/g, '').slice(0, 2))}
          placeholder="5"
          inputMode="numeric"
          maxLength={2}
          invalido={Boolean(errorDe('corte'))}
        />
      </Campo>

      <Campo
        label="Días para pagar"
        ayuda="Cuántos días te da el banco desde el corte para pagar. Lo más común en México son 20."
        error={errorDe('plazo')}
      >
        <Texto
          value={plazo}
          onChange={(v) => setPlazo(v.replace(/\D/g, '').slice(0, 2))}
          placeholder="20"
          inputMode="numeric"
          maxLength={2}
          invalido={Boolean(errorDe('plazo'))}
        />
      </Campo>

      <Campo
        label="Cómo se cuentan esos días"
        ayuda="Los días hábiles saltan sábados, domingos y días inhábiles bancarios mexicanos —incluidos Jueves y Viernes Santo—. La diferencia con los naturales son varios días y un recargo."
      >
        <Segmentos opciones={TIPOS_PLAZO} value={tipoPlazo} onChange={setTipoPlazo} />
      </Campo>

      <Interruptor
        label="El gasto del día del corte entra en ese estado de cuenta"
        ayuda="Apagado: un gasto del mismo día del corte se va al periodo siguiente, que es como lo manejan casi todos los bancos en México. Enciéndelo solo si tu estado de cuenta dice lo contrario."
        value={inclusivo}
        onChange={setInclusivo}
      />

      <Campo
        label="¿De quién es?"
        ayuda="Una tarjeta personal solo la ve su dueño; la del fondo común la ve todo el hogar y su pago se reparte."
      >
        <Segmentos opciones={opcionesDueno} value={dueno} onChange={setDueno} />
      </Campo>

      {card ? (
        <ZonaArchivo
          card={card}
          confirmando={confirmandoArchivo}
          archivando={archivar.isPending}
          onPedirConfirmacion={() => {
            haptic('warning')
            setConfirmandoArchivo(true)
          }}
          onCancelar={() => setConfirmandoArchivo(false)}
          onArchivar={() =>
            archivar.mutate(card.id, {
              onSuccess: () => {
                haptic('success')
                onCerrar()
              },
              onError: () => {
                haptic('error')
                setConfirmandoArchivo(false)
              },
            })
          }
        />
      ) : null}
    </FormSheet>
  )
}

/**
 * Archivar, nunca borrar.
 *
 * Los movimientos y estados de cuenta de la tarjeta son historia contable:
 * eliminarlos descuadraría los totales de meses ya cerrados. La confirmación es
 * en dos pasos y dentro de la misma pantalla, porque un diálogo del sistema se
 * responde por reflejo sin llegar a leerlo.
 */
function ZonaArchivo({
  card,
  confirmando,
  archivando,
  onPedirConfirmacion,
  onCancelar,
  onArchivar,
}: {
  card: CreditCard
  confirmando: boolean
  archivando: boolean
  onPedirConfirmacion: () => void
  onCancelar: () => void
  onArchivar: () => void
}) {
  if (!confirmando) {
    return (
      <button
        type="button"
        onClick={onPedirConfirmacion}
        className="mt-2 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-card border border-line bg-surface text-[15px] font-semibold text-expense-text active:scale-[0.98]"
      >
        <ArchiveIcon size={18} />
        Archivar tarjeta
      </button>
    )
  }

  return (
    <div className="mt-2 flex flex-col gap-3 rounded-card bg-expense-soft p-3.5">
      <p className="flex items-start gap-2 text-[13px] leading-snug text-expense-text">
        <WarningIcon size={16} weight="fill" className="mt-0.5 shrink-0" />
        <span>
          <strong>{card.name}</strong> dejará de aparecer en tus listas y ya no podrás pagar
          con ella. Sus movimientos y estados de cuenta se conservan: el historial no se
          toca.
          {card.pendingPaymentCents > 0
            ? ' Ojo: esta tarjeta todavía tiene un pago pendiente.'
            : ''}
        </span>
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancelar}
          className="min-h-[48px] flex-1 rounded-card border border-line bg-surface text-[14px] font-semibold text-ink active:scale-[0.98]"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onArchivar}
          disabled={archivando}
          className="min-h-[48px] flex-1 rounded-card bg-expense text-[14px] font-semibold text-white active:scale-[0.98] disabled:opacity-70"
        >
          {archivando ? 'Archivando…' : 'Sí, archivar'}
        </button>
      </div>
    </div>
  )
}

/** Nombres de campo del servidor, para colgar su mensaje del campo correcto. */
const CAMPO_API = {
  nombre: 'name',
  last4: 'last4',
  limite: 'creditLimitCents',
  corte: 'statementDay',
  plazo: 'paymentTermDays',
} as const

/**
 * Réplica de las reglas del servidor, para atajar antes de gastar una petición.
 * El servidor sigue siendo la autoridad: sus mensajes por campo se pintan igual
 * si algo se cuela.
 */
function validar({
  nombre,
  last4,
  limiteCents,
  corte,
  plazo,
}: {
  nombre: string
  last4: string
  limiteCents: number
  corte: string
  plazo: string
}): Record<'nombre' | 'last4' | 'limite' | 'corte' | 'plazo', string | undefined> {
  const dia = Number(corte)
  const dias = Number(plazo)
  return {
    nombre: nombre.trim().length < 2 ? 'Ponle nombre a la tarjeta.' : undefined,
    last4:
      last4 !== '' && !/^\d{4}$/.test(last4)
        ? 'Escribe los cuatro dígitos completos, o déjalo vacío.'
        : undefined,
    limite: limiteCents <= 0 ? 'El límite de crédito debe ser mayor a cero.' : undefined,
    corte:
      corte === '' || !Number.isInteger(dia) || dia < 1 || dia > 31
        ? 'El día de corte va del 1 al 31.'
        : undefined,
    plazo:
      plazo === '' || !Number.isInteger(dias) || dias < 0 || dias > 90
        ? 'El plazo va de 0 a 90 días.'
        : undefined,
  }
}

function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isForbidden) return 'Solo quien administra el hogar puede hacer este cambio.'
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos guardar la tarjeta. Inténtalo de nuevo.'
}
